import * as React from 'react';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { cn } from '@/lib/utils/cn';

/**
 * RFI state-machine chip — maps the backend status enum to the posture
 * vocabulary the design uses.
 *
 * Wire enum (T-0035): open / response_received / closed / escalated.
 * Visible vocabulary (prototype screens-rfi.js):
 *   open               → "Open"        (at-risk amber, attention-needed)
 *   response_received  → "Responded"   (at-risk amber → leaning healthy)
 *   closed             → "Closed"      (healthy green, terminal)
 *   escalated          → "Escalated"   (critical red, terminal-and-bad)
 *
 * The prototype shows a separate "Overdue" chip; that's a derived state
 * (`due_by < now` while still `open`), not a wire status. The screen
 * computes it locally when it has a due_by field — for now we surface the
 * backend status only, but the helper accepts an optional `overdue` flag.
 */

const RFI_STATUS_TO_POSTURE: Record<string, { status: PostureStatus; label: string }> = {
  open: { status: 'at-risk', label: 'Open' },
  response_received: { status: 'at-risk', label: 'Responded' },
  closed: { status: 'healthy', label: 'Closed' },
  escalated: { status: 'critical', label: 'Escalated' },
};

export interface RfiStatusChipProps {
  status: string;
  /** Optional overdue flag — overrides the chip to render as critical. */
  overdue?: boolean;
  /**
   * Optional label override — used by the summary counts ("12 open" etc.)
   * to render the chip with a count in place of the canonical label.
   */
  children?: React.ReactNode;
  className?: string;
}

export function RfiStatusChip({ status, overdue, children, className }: RfiStatusChipProps) {
  if (overdue) {
    return (
      <PostureChip status="critical" className={className}>
        {children ?? 'Overdue'}
      </PostureChip>
    );
  }
  const mapped = RFI_STATUS_TO_POSTURE[status];
  if (!mapped) {
    return (
      <PostureChip status="unassessed" className={cn(className)}>
        {children ?? status}
      </PostureChip>
    );
  }
  return (
    <PostureChip status={mapped.status} className={className}>
      {children ?? mapped.label}
    </PostureChip>
  );
}
